'use client';

import { useEffect, useMemo, useState } from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { defaultCountdownUnits, highlightClasses } from './constants';
import {
  formatCountdown,
  formatDeadline,
  getRemainingMs,
  shouldShowCountdown,
} from './formatters';
import { TRANSLATIONS } from './i18n';
import type { UrgencyBarProps } from './types';

export function useUrgencyBar({
  endDate,
  countdownUnits = defaultCountdownUnits,
  flashDealLabel,
  highlight = 'red',
  initialNow,
  showFlashDeal = false,
}: UrgencyBarProps) {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const [remainingMs, setRemainingMs] = useState(() =>
    getRemainingMs(endDate, initialNow),
  );
  const isEnded = remainingMs === 0;
  const showCountdown = shouldShowCountdown(remainingMs);
  const shouldTick = showCountdown && !isEnded;
  const styles = highlightClasses[highlight];
  const deadline = useMemo(
    () => formatDeadline(endDate, context.lang),
    [context.lang, endDate],
  );
  const compactDeadline = useMemo(
    () => formatDeadline(endDate, context.lang, false),
    [context.lang, endDate],
  );

  const deadlineText = isEnded
    ? staticText.offer_ended
    : staticText.offer_ends.replace('{date}', deadline);
  const secondaryText =
    !isEnded && compactDeadline
      ? staticText.ends.replace('{date}', compactDeadline)
      : deadlineText;
  const visibleFlashDealLabel =
    showFlashDeal && !isEnded
      ? (flashDealLabel?.trim() ?? '') || staticText.flash_deal_default_label
      : undefined;

  useEffect(() => {
    if (!shouldTick) return;

    const interval = window.setInterval(
      () => setRemainingMs(getRemainingMs(endDate, Date.now())),
      1000,
    );
    return () => window.clearInterval(interval);
  }, [endDate, shouldTick]);

  return {
    backgroundClassName: styles.background,
    countdown: formatCountdown(remainingMs, countdownUnits),
    countdownUnits,
    deadlineText,
    flashDealLabel: visibleFlashDealLabel,
    secondaryText,
    showCountdown,
    textClassName: styles.text,
  };
}
